
/** Flow: Home → Details → Instructions (+ posture) → Scan → Results */
export const ROUTES = {
  HOME: '/',
  DETAILS: '/details',
  INSTRUCTIONS: '/instructions',
  SCAN: '/scan',
  RESULTS: '/results',
} as const;

export const PRIVACY_POLICY_URL = 'https://www.sugarfree-india.com/policies/privacy-policy' as const;

/** SDK face-frame stroke colour — Sugar Free Cobalt Blue */
export const SCAN_FRAME_COLOR = '#FFDD00' as const;

/** Scan completion progress arc — Sugar Free Lemon Yellow */
export const SCAN_PROGRESS_COLOR = '#FFDD00' as const;

/** Toggle SF Gold segment ring overlay during face scan */
export const SHOW_SCAN_FACE_PROGRESS_RING = false;

export const EMAIL_RESULTS_HINT = 'Share your mail id so we can share results.' as const;

export const MOBILE_WHATSAPP_HINT =
  'Share your WhatsApp number to receive personalised results.' as const;

/** Age is picked from a dropdown (18–120) instead of a date of birth. */
export const MIN_SCAN_AGE_YEARS = 18;
export const MAX_SCAN_AGE_YEARS = 120;

/** Home hero copy — matches the "How sweet is your face?" KV / App UI design. */
export const HOME_HERO = {
  presents: 'Presents',
  /** Line breaks as set in the KV. */
  headlineLines: ['How', 'sweet', 'is your', 'face?'],
  subheadline: 'Scan and see for yourself in a minute.',
  body: 'Because your face is the window to your health',
  cta: 'Start Now',
} as const;

export const HOME_SCAN_DISCLAIMER =
  'This Face Scan is for general wellness and informational purposes only. It is not a medical device and does not provide a clinical diagnosis. Results are estimates and should not replace advice from a qualified healthcare professional. Seek medical attention for any health concerns.' as const;

// TODO(copy-approval): diabetes-focused FAQ answers are draft copy pending
// brand/medical sign-off (feedback: "Refer these FAQs … tweak W.R.T Scan for Diabetes").
export const HOME_SCAN_FAQ = [
  {
    id: 'how-it-works',
    question: 'How does the Sugar Free Face Scan work?',
    answer:
      'The scan uses your phone camera to read tiny colour changes in your skin caused by your pulse (a technique called rPPG). In about a minute it estimates your diabetes risk score, predicted HbA1c range and key vitals — no needles, no lab visit.',
  },
  {
    id: 'is-it-a-test',
    question: 'Is this a diabetes test?',
    answer:
      'No. It is a wellness screening tool that estimates your risk. It does not diagnose diabetes. If your risk comes out moderate or high, please get a blood test (such as HbA1c or fasting glucose) done with your healthcare provider.',
  },
  {
    id: 'what-info',
    question: 'What will my results show?',
    answer:
      'Your diabetes risk score (low, moderate or high), a predicted HbA1c range, and a few key vitals — heart rate, BMI and body fat percentage.',
  },
  {
    id: 'equipment',
    question: 'Do I need any special equipment?',
    answer:
      'No. All you need is a phone or laptop with a front camera, a stable internet connection and good, even lighting on your face.',
  },
  {
    id: 'data-privacy',
    question: 'Is my data private and secure?',
    answer:
      'Your personal details and scan results are handled securely and used only to generate your health insights. Data is processed in line with our Privacy Policy. We do not sell your personal information.',
  },
] as const;

/** Credentials and validations shown in the home footer to build trust (approved). */
export const SHOW_CERTIFICATIONS = true;

export const CERTIFICATIONS = [
  {
    // Source: feedback/CareplixHBA1CStudy_IPGMERReport — an interim study funded
    // by CarePlix. Accuracy stats (MAE 0.72%, r = 0.41) deliberately left out.
    id: 'ipgmer-study',
    title: 'Clinically studied',
    description:
      'Type 2 diabetes screening with CarePlix Vitals studied at IPGME&R and SSKM Hospital, Kolkata',
    detail: '1,057 participants (interim study)',
  },
  {
    id: 'cdsco',
    title: 'CDSCO',
    description: 'Registered as a Class B medical device under CDSCO',
    detail: 'License No: WB/M/MD/002063',
  },
  {
    id: 'usfda',
    title: 'USFDA 21 CFR Part 11',
    description: 'Registered as a Class 1 medical device under USFDA',
  },
  {
    id: 'hipaa',
    title: 'HIPAA',
    description: 'Compliant with the Health Insurance Portability and Accountability Act',
  },
  {
    id: 'ce',
    title: 'CE',
    description: 'Awaiting Conformité Européenne (CE) certification',
  },
] as const;

export const POSTURE_HEADING = {
  title: 'What’s that pose you’re holding?',
  subtitle: 'Your current physical state affects cardiovascular accuracy.',
  label: 'Select what you’re doing right now:',
} as const;

export const POSTURE_OPTIONS = [
  { value: 'resting_sitting', label: 'Resting / Sitting', emoji: '🪑' },
  { value: 'standing', label: 'Standing', emoji: '🧍' },
  { value: 'walking', label: 'Just finished a walk', emoji: '🚶' },
  { value: 'exercising', label: 'Just finished exercising', emoji: '🏃' },
] as const;

export const POSTURE_API_MAP = {
  resting_sitting: 'resting',
  standing: 'standing',
  walking: 'walking',
  exercising: 'exercising',
} as const;

/**
 * Text-only guidelines (no photos): the old images had outdated pointer copy
 * baked in and non-Indian faces. Pointers use the revised copy from feedback.
 */
export const SCAN_GUIDELINES = [
  {
    id: 'lighting',
    emoji: '💡',
    title: 'That face deserves some spotlight',
    pointers: ['Bright, even, white light', 'Remove masks, eyewear or anything covering your face'],
  },
  {
    id: 'steady-phone',
    emoji: '📱',
    title: 'Don’t blur the sweetness',
    pointers: [
      'Bring your face closer to the camera',
      'Look straight',
      'Hold your phone steady',
      'Stay calm and wait for the scan to complete',
    ],
  },
  {
    id: 'camera-permission',
    emoji: '📸',
    title: 'Say sweet!',
    pointers: ['Tap “Allow” when your browser asks to use the camera'],
  },
] as const;

/** Rotated on the scan screen while preparing and scanning. */
export const SCAN_FACTS = [
  'Did you know? Jaggery is still sugar — just in a different form.',
  '2 tsp in one chai? That’s already ~30% of your daily limit.',
  'Think you don’t eat much sugar? Check what’s hiding in your pantry.',
  'Why give up sweetness when you can change what makes it sweet?',
  'Stevia gives natural sweetness, straight from a leaf.',
  'What if your next cup stayed sweet, but skipped the sugar?',
  'Moderating sugar doesn’t make it disappear.',
] as const;

export const SCAN_FACT_INTERVAL_MS = 5000;

/** Cheeky one-liners under the diabetes risk ring, keyed by risk level. */
export const DIABETES_RISK_MESSAGES = {
  low: 'Keep smiling. You’ve got the sweetness in check.',
  moderate: 'Looks like your sweet side needs to ease up a little.',
  high: 'Your face is dropping some seriously sweet hints.',
} as const;

/** Wellness score labels — only used when the scan returns no diabetes data. */
export const HEALTH_SCORE_THRESHOLDS = {
  EXCELLENT: 85,
  GOOD: 70,
  FAIR: 50,
} as const;

export const DIABETES_RISK_EMOJI = {
  low: '😊',
  moderate: '😬',
  high: '😟',
} as const;

export const RESULT_TABS = [
  { id: 'diabetes', label: 'Diabetes', emoji: '🩸' },
  { id: 'vitals', label: 'Vitals', emoji: '❤️' },
] as const;

export const METRIC_TOOLTIPS: Record<string, string> = {
  heartRate: 'Number of heart beats per minute.',
  respirationRate: 'Number of breaths taken per minute.',
  oxygenSaturation: 'Percentage of oxygen in your blood.',
  systolicBP: 'Pressure when the heart beats.',
  diastolicBP: 'Pressure when the heart rests between beats.',
  diabetesRiskScore:
    'Estimated diabetes risk score from glucose-related scan signals. Lower scores indicate lower risk.',
  sdnn: 'Standard deviation of NN intervals — overall HRV.',
  rmssd: 'Root mean square of successive differences — short-term HRV.',
  pnn50: 'Percentage of successive RR intervals differing by more than 50 ms.',
  cardiacOutput: 'Volume of blood pumped by the heart per minute.',
  bmi: 'Body mass index from height and weight.',
  bloodVolumeMl: 'Estimated total blood volume.',
  totalBodyWaterLitres: 'Estimated total body water.',
  bodyWaterPercent: 'Percentage of body weight that is water.',
  totalBodyFatPercent: 'Estimated percentage of body fat.',
  bodyWeightKg: 'Estimated body weight in kilograms.',
  stressIndex: 'Composite index of autonomic stress response.',
};
